/**
 * Tunable settings for the page translator. Nothing here has side effects.
 */

// ── Backend ────────────────────────────────────────────────────────────────────
// POST <API base>/translation/v1/translate-text  { sourceLanguage, targetLanguage, textData: [...] }
//   -> [ { source, target }, ... ]  (same order as textData). No login required.
export const TRANSLATE_PATH = '/translation/v1/translate-text';
// GET <API base>/translation/v1/languages -> [ { code, name, native_name, direction } ]
// The languages offered besides English. No login required.
export const LANGUAGES_PATH = '/translation/v1/languages';
export const REQUEST_TIMEOUT_MS = 30000;
export const MAX_RETRIES = 3;

/**
 * Development switch: `REACT_APP_TRANSLATION_MOCK=true` returns fake translations ("[ar] text")
 * without calling the backend, so the whole flow can be tried before the API is connected.
 */
export const USE_MOCK_TRANSLATION = process.env.REACT_APP_TRANSLATION_MOCK === 'true';

// ── Batching and rate limit ────────────────────────────────────────────────────
export const CHUNK_SIZE = 25;                       // strings per request
export const MAX_REQUESTS_PER_MINUTE = 60;          // backend limit, per user
export const MIN_REQUEST_GAP_MS = Math.ceil(60000 / MAX_REQUESTS_PER_MINUTE);
export const BATCH_DEBOUNCE_MS = 250;               // wait this long to group nearby text into one batch
export const ERROR_COOLDOWN_MS = 15000;             // pause after a failed batch before trying again

// ── Scroll-by-scroll translation ───────────────────────────────────────────────
// Text is translated when it is within this distance of the visible screen.
export const VIEWPORT_MARGIN = '300px 0px';

// ── Persistence ────────────────────────────────────────────────────────────────
export const LANGUAGE_STORAGE_KEY = 'preferredLanguage';
export const LANGUAGE_DIRECTION_STORAGE_KEY = 'preferredLanguageDirection';
export const LANGUAGE_LIST_STORAGE_KEY = 'vrfp_translation_languages_v1';
export const CACHE_STORAGE_KEY = 'vrfp_translation_cache_v1';
export const CACHE_MAX_ENTRIES = 5000;
export const CACHE_MAX_TEXT_LENGTH = 600;           // longer strings are translated but not cached

// ── What is translated ─────────────────────────────────────────────────────────
// Attributes translated in addition to visible text.
export const TRANSLATABLE_ATTRIBUTES = Object.freeze(['placeholder', 'title']);

// Anything inside these is NEVER translated (text and attributes): the document editor, written
// RFP content, people's names and emails. Add the `dont-translate` class to opt other areas out.
export const NEVER_TRANSLATE_SELECTOR = [
    '.dont-translate',
    '.notranslate',
    '[translate="no"]',
    '[contenteditable="true"]',
    '.ProseMirror',
    '.unified-seamless-editor-wrapper',
    '.seamless-tiptap-content',
    '.sb-user-name',
    '.sb-avatar',
    '.user-name',
    '.user-email',
    '.db-team-name',
].join(',');

// Text inside these tags is never translated (their attributes still can be).
export const SKIP_TEXT_SELECTOR = 'script,style,noscript,textarea,code,pre,kbd,samp,svg';
